// @flow
import changeWindowLocation from 'app/common/lib/util/changeWindowLocation';
import CONST from 'app/common/lib/Const';
import CONTAINER_NAME from 'app/common/react/components/growth/unauth/lib/loggingContainerConstants';
import multipleAccountStorage from 'app/common/lib/multipleAccountStorage';
import type { AuthViewer } from 'app/packages/pinterest-flow-types/viewer';
import type { BusinessUser } from 'app/common/lib/BusinessOwnershipUtils';
import uuidV4 from 'uuid/v4';
import Impressions, { LOW_PRIORITY, HIGH_PRIORITY } from 'app/packages/impression-logging';
import trackRegisterAction, {
  trackRegisterActionWithAuxData,
} from 'app/common/lib/util/trackRegisterAction';

const accountSwitcherMetric = 'web_account_switcher';

export const MAX_ACCOUNTS = 4;

export type MultipleAccountsUser = {
  id: string,
  email: string,
  username: string,
  fullName?: string,
  full_name?: string,
  imageMediumUrl?: string,
  image_medium_url?: string,
  imageLargeUrl?: string,
  image_large_url?: string,
  partner?: {
    biz_ownership_email: string,
    isLinkedBusiness?: boolean,
    is_linked_business: boolean,
    is_create?: boolean,
  },
};

export const getStoredUserAccounts = (): { [key: string]: MultipleAccountsUser } =>
  multipleAccountStorage.getAccountsJSON();

export const getNumberOfUsersLoggedIn = () => Object.keys(getStoredUserAccounts()).length;

const createGroupId = () => {
  // Generate a 'unique' identifier for this multiple account group
  const groupId = uuidV4();
  trackRegisterActionWithAuxData(`${accountSwitcherMetric}.utils.group_created`, {
    group_id: groupId,
    group_count: getNumberOfUsersLoggedIn(),
  });
  return groupId;
};

export const hasStoredUserAccount = () => !!multipleAccountStorage.getAccounts();

export const getAccountSwitcherStorageItems = () => ({
  [CONST.MULTIPLE_ACCOUNTS]: multipleAccountStorage.getAccountsJSON(),
  [CONST.MULTIPLE_ACCOUNTS_GROUP]: multipleAccountStorage.getGroupId(),
});

export const trackMultipleAccounts = (metricName: string, auxData: ?Object, container?: string) => {
  let groupId = multipleAccountStorage.getGroupId();
  // If no group id, create and set one
  if (!groupId) {
    groupId = createGroupId();
    multipleAccountStorage.setGroupId(groupId);
  }
  const defaultAuxData = {
    group_id: groupId,
    group_count: getNumberOfUsersLoggedIn(),
  };
  let presentationState = '';
  if (container) {
    presentationState = container === CONTAINER_NAME.ACCOUNT_SWITCHER_MODAL ? 'modal.' : 'flyout.';
  }
  trackRegisterActionWithAuxData(
    `${accountSwitcherMetric}.${presentationState}${metricName}`,
    Object.assign({}, auxData, defaultAuxData),
  );
};

const getUserValues = (user: Object) => {
  const newUser = {
    id: user.id,
    username: user.username,
    email: user.email,
    fullName: user.fullName || user.full_name,
    imageMediumUrl: user.imageMediumUrl || user.image_medium_url,
    imageLargeUrl: user.imageLargeUrl || user.image_large_url,
  };
  // Check that none of the values are undefined
  if (!newUser.fullName || !newUser.imageMediumUrl || !newUser.imageLargeUrl) {
    trackMultipleAccounts('utils.incomplete_user_values');
  }

  if (user.partner && user.partner.is_create) {
    return {
      ...newUser,
      partner: {
        is_create: user.partner.is_create,
      },
    };
  }

  // We still return the user object since the user has already been authenticated
  return newUser;
};

export const addUsersLoggedIn = (user: AuthViewer, method?: string, container?: string) => {
  const usersLoggedIn = multipleAccountStorage.getAccountsJSON();
  usersLoggedIn[user.id] = getUserValues(user);
  multipleAccountStorage.setAccounts(JSON.stringify(usersLoggedIn));
  if (method && container) {
    trackMultipleAccounts(
      'new_account_add_success',
      {
        method,
        user_added: user.id,
      },
      container,
    );
  }
};

export const removeUserLoggedIn = (userId: string) => {
  const usersLoggedIn = multipleAccountStorage.getAccountsJSON();
  delete usersLoggedIn[userId];
  multipleAccountStorage.setAccounts(JSON.stringify(usersLoggedIn));
};

export const initializeAccounts = (viewer: AuthViewer, businessUser: ?BusinessUser) => {
  if (!hasStoredUserAccount() || (viewer && !(viewer.id in getStoredUserAccounts()))) {
    // storage not initialized
    if (viewer.partner && viewer.partner.is_linked_business && businessUser) {
      addUsersLoggedIn({
        ...viewer,
        email:
          businessUser.partner && businessUser.partner.is_linked_business
            ? businessUser.partner.biz_ownership_email
            : '',
      });

      return;
    }

    addUsersLoggedIn(viewer);
  }
};

export const handleAccountSwitchSuccess = (url: string, user_id?: string, container: string) => {
  trackMultipleAccounts(
    'switch_account_success',
    {
      destination_user_id: user_id,
    },
    container,
  );

  // After we have logged in the user successfully, redirect to desired page
  changeWindowLocation(url);
};

export const handleAccountSwitchFail = (user_id: string, container: string) => {
  // resume Impressions on failure to switch accounts
  Impressions.resume(HIGH_PRIORITY);

  trackMultipleAccounts(
    'switch_account_failure',
    {
      destination_user_id: user_id,
    },
    container,
  );
};

/*
 * Handle a dismiss on the modal to add another account
 */
export const handleLoginSecondAccountFailure = (container: string) => {
  // resume Impressions on failure to switch accounts with LOW_PRIORITY
  Impressions.resume(LOW_PRIORITY);
  trackRegisterAction(`web.dismiss_account_switcher.${container}`);
};

export default undefined;
